import React, { useState, useEffect } from 'react';
import '../styles/FilesDisplay.css';
import { api } from '../utils/apiHelper';

const FilesDisplay = ({ files = [], setFiles, onRemove, onPlayUploadSuccess, projectId, theId }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [clickCounts, setClickCounts] = useState(new Map());

  const handleMultiFileSelect = (fileId) => {
    setClickCounts((prevCounts) => {
      const newCounts = new Map(prevCounts);
      const count = newCounts.get(fileId) || 0;
      const nextCount = count + 1;

      if (nextCount === 2) {
        setSelectedFiles((prev) => {
          const updated = new Set(prev);
          updated.add(fileId);
          return updated;
        });
      }

      if (nextCount === 3) {
        const confirmDelete = window.confirm("Are you sure you want to delete this file?");
        if (confirmDelete) {
          onRemove(fileId);
          newCounts.delete(fileId);
          setSelectedFiles((prev) => {
            const updated = new Set(prev);
            updated.delete(fileId);
            return updated;
          });
        } else {
          newCounts.set(fileId, 2); // stay selected if cancel
        }
      } else {
        newCounts.set(fileId, nextCount);
      }

      return newCounts;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".display-file-item")) {
        setClickCounts(new Map());
        setSelectedFiles(new Set());
      }
    };

    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const handleAddToPlaylist = async () => {
    if (selectedFiles.size === 0) {
      alert('No files selected.');
      return;
    }

    setUploading(true);

    try {
      const filesToTransfer = files.filter((file) => selectedFiles.has(file.fileId));
      const transferPayload = filesToTransfer.map((file, index) => ({
        sourceKey: file.key,
        destinationKey: file.key.replace('files/', 'playlists/'),
        userId: theId,
        projectId,
        fileName: file.fileName,
        seqPos: index + 1,
        slotPosition: '',
        duration: file.duration,
        frameRate: file.frameRate,
        resolution: file.resolution,
        createdAt: new Date().toISOString(),
      }));

      const response = await api('/transfer-to-playlist', 'POST', { files: transferPayload });

      if (!response.success) throw new Error('Failed to transfer files');

      if (onPlayUploadSuccess) onPlayUploadSuccess();

      setSelectedFiles(new Set());
      alert('Files added to playlist.');
    } catch (error) {
      console.error(error);
      alert('Error: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="files-container">
      <ul className="display-files-list">
        {files.length > 0 ? (
          files.map((file, index) => {
            const fileExtension = file.fileName?.toLowerCase().split('.').pop() || '';
            const clickCount = clickCounts.get(file.fileId) || 0;

            const className =
              clickCount === 2
                ? 'display-file-item selected'
                : clickCount === 3
                ? 'display-file-item ready-to-delete'
                : 'display-file-item';

            return (
              <li
                key={file.fileId}
                className={className}
                onClick={() => handleMultiFileSelect(file.fileId)}
              >
                <strong>{index + 1}. </strong>
                {file.url ? (
                  <>
                    {['png', 'jpg', 'jpeg'].includes(fileExtension) ? (
                      <img src={file.url} alt={file.fileName || file.key} />
                    ) : ['mp4', 'mov'].includes(fileExtension) ? (
                      <video width="100%" height="auto" controls>
                        <source src={file.url} type={`video/${fileExtension}`} />
                        Your browser does not support the video tag.
                      </video>
                    ) : ['mp3', 'wav'].includes(fileExtension) ? (
                      <audio controls>
                        <source src={file.url} type={`audio/${fileExtension}`} />
                        Your browser does not support the audio tag.
                      </audio>
                    ) : (
                      <a href={file.url} target="_blank" rel="noopener noreferrer">
                        {file.fileName || file.key}
                      </a>
                    )}
                  </>
                ) : (
                  <p>Empty Slot</p>
                )}
              </li>
            );
          })
        ) : (
          <p>No files available</p>
        )}
      </ul>

      {selectedFiles.size > 0 && (
        <button
          className="add-to-playlist-button"
          onClick={handleAddToPlaylist}
          disabled={uploading}
        >
          {uploading ? 'Processing...' : 'Add to Playlist'}
        </button>
      )}
    </div>
  );
};

export default FilesDisplay;
